import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import {
  Users, DollarSign, FolderOpen, TrendingUp, Loader2,
  BarChart3, Coins, PieChart, Plus, Pencil, Trash2, X
} from 'lucide-react';
import { ExportImportButtons } from '../components/ExportImportButtons';
import { useExportImport } from '../hooks/useExportImport';
import CompanyDetailsCard from '../components/CompanyDetailsCard';
import CapTablePreviewCard from '../components/CapTablePreviewCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);
const fmtCompact = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(v || 0);

// ── NAV Record Dialog ──
function NavDialog({ open, onOpenChange, record, companyId, onSave }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    total_assets: '', total_liabilities: '', shares_outstanding: '', nav: '', share_value: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (record) {
        setForm({
          date: record.date || '', total_assets: record.total_assets != null ? String(record.total_assets) : '',
          total_liabilities: record.total_liabilities != null ? String(record.total_liabilities) : '',
          shares_outstanding: record.shares_outstanding != null ? String(record.shares_outstanding) : '',
          nav: record.nav != null ? String(record.nav) : '',
          share_value: record.share_value != null ? String(record.share_value) : '',
          notes: record.notes || ''
        });
      } else {
        setForm({ date: new Date().toISOString().slice(0, 10), total_assets: '', total_liabilities: '', shares_outstanding: '', nav: '', share_value: '', notes: '' });
      }
    }
  }, [record, open]);

  // Auto-calculate NAV & share value
  useEffect(() => {
    const assets = parseFloat(form.total_assets) || 0;
    const liab = parseFloat(form.total_liabilities) || 0;
    const shares = parseFloat(form.shares_outstanding) || 0;
    if (form.total_assets && form.total_liabilities) {
      const nav = assets - liab;
      setForm(p => ({
        ...p, nav: nav.toFixed(2),
        share_value: shares > 0 ? (nav / shares).toFixed(2) : p.share_value
      }));
    }
  }, [form.total_assets, form.total_liabilities, form.shares_outstanding]);

  const handleSubmit = async () => {
    if (!form.date) { toast.error('Date is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        company_id: companyId, date: form.date,
        total_assets: form.total_assets ? parseFloat(form.total_assets) : null,
        total_liabilities: form.total_liabilities ? parseFloat(form.total_liabilities) : null,
        nav: form.nav ? parseFloat(form.nav) : null,
        shares_outstanding: form.shares_outstanding ? parseFloat(form.shares_outstanding) : null,
        share_value: form.share_value ? parseFloat(form.share_value) : null,
        notes: form.notes || null
      };
      if (record) {
        await axios.put(`${API_URL}/api/nav-records/${record.id}`, payload, { withCredentials: true });
        toast.success('NAV record updated');
      } else {
        await axios.post(`${API_URL}/api/nav-records`, payload, { withCredentials: true });
        toast.success('NAV record added');
      }
      onSave(); onOpenChange(false);
    } catch { toast.error('Failed to save'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{record ? 'Edit NAV Record' : 'Add NAV Record'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Date *</Label>
            <Input type="date" value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} data-testid="nav-date-input" />
          </div>
          <div>
            <Label>Total Assets</Label>
            <Input type="number" step="0.01" value={form.total_assets} onChange={e => setForm(p => ({...p, total_assets: e.target.value}))} placeholder="0.00" data-testid="nav-assets-input" />
          </div>
          <div>
            <Label>Total Liabilities</Label>
            <Input type="number" step="0.01" value={form.total_liabilities} onChange={e => setForm(p => ({...p, total_liabilities: e.target.value}))} placeholder="0.00" data-testid="nav-liabilities-input" />
          </div>
          <div>
            <Label>NAV (auto-calculated)</Label>
            <Input type="number" step="0.01" value={form.nav} onChange={e => setForm(p => ({...p, nav: e.target.value}))} placeholder="Assets - Liabilities" data-testid="nav-value-input" className="bg-gray-50" />
          </div>
          <div>
            <Label>Shares Outstanding</Label>
            <Input type="number" step="0.01" value={form.shares_outstanding} onChange={e => setForm(p => ({...p, shares_outstanding: e.target.value}))} placeholder="0" data-testid="nav-shares-input" />
          </div>
          <div>
            <Label>Share Value (auto-calculated)</Label>
            <Input type="number" step="0.01" value={form.share_value} onChange={e => setForm(p => ({...p, share_value: e.target.value}))} placeholder="NAV / Shares" data-testid="nav-sharevalue-input" className="bg-gray-50" />
          </div>
          <div>
            <Label>Notes</Label>
            <Input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Optional notes" data-testid="nav-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="save-nav-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : record ? 'Update' : 'Add Record'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── NAV Analytics Section (Veriquant only) ──
function NavAnalyticsSection({ navData, companyId, onRefresh }) {
  const [addOpen, setAddOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const latest = navData?.latest;
  const history = navData?.history || [];
  const chartData = history.map(r => ({
    date: r.date,
    NAV: r.nav,
    'Share Value': r.share_value
  }));

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/nav-records/${deleteTarget.id}`, { withCredentials: true });
      toast.success('NAV record deleted');
      setDeleteTarget(null);
      onRefresh();
    } catch { toast.error('Delete failed'); }
  };

  // Calculate change from previous record
  const prevNav = history.length >= 2 ? history[history.length - 2]?.nav : null;
  const navChange = latest?.nav && prevNav ? ((latest.nav - prevNav) / prevNav * 100) : null;
  const prevShare = history.length >= 2 ? history[history.length - 2]?.share_value : null;
  const shareChange = latest?.share_value && prevShare ? ((latest.share_value - prevShare) / prevShare * 100) : null;

  return (
    <>
      {/* NAV & Share Value Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-nav">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Net Asset Value</p>
              {latest?.nav != null ? (
                <>
                  <p className="text-2xl font-semibold mt-1">{fmtCompact(latest.nav)}</p>
                  {navChange !== null && (
                    <p className={`text-xs mt-0.5 font-medium ${navChange >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                      {navChange >= 0 ? '+' : ''}{navChange.toFixed(1)}% from last
                    </p>
                  )}
                </>
              ) : (
                <p className="text-lg text-gray-300 mt-1">No data yet</p>
              )}
            </div>
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-teal-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-share-value">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Share Value</p>
              {latest?.share_value != null ? (
                <>
                  <p className="text-2xl font-semibold mt-1">{fmtCurrency(latest.share_value)}</p>
                  {shareChange !== null && (
                    <p className={`text-xs mt-0.5 font-medium ${shareChange >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                      {shareChange >= 0 ? '+' : ''}{shareChange.toFixed(1)}% from last
                    </p>
                  )}
                </>
              ) : (
                <p className="text-lg text-gray-300 mt-1">No data yet</p>
              )}
            </div>
            <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
              <Coins className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-shares-outstanding">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Shares Outstanding</p>
              {latest?.shares_outstanding != null ? (
                <p className="text-2xl font-semibold mt-1">{new Intl.NumberFormat('en-US').format(latest.shares_outstanding)}</p>
              ) : (
                <p className="text-lg text-gray-300 mt-1">No data yet</p>
              )}
            </div>
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <PieChart className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-shareholders">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Shareholders</p>
              <p className="text-2xl font-semibold mt-1">{navData?.total_shareholders || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-rose-600" />
            </div>
          </div>
        </div>
      </div>

      {/* NAV & Share Value Chart */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-medium text-gray-900">NAV & Share Value History</h2>
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-nav-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Record
          </Button>
        </div>
        <div className="p-4">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="nav" tick={{ fontSize: 11 }} tickFormatter={v => fmtCompact(v)} />
                <YAxis yAxisId="share" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                <Tooltip formatter={(v, name) => name === 'NAV' ? fmtCurrency(v) : `$${Number(v).toFixed(2)}`} />
                <Legend />
                <Line yAxisId="nav" type="monotone" dataKey="NAV" stroke="#0d9488" strokeWidth={2} dot={{ r: 3 }} />
                <Line yAxisId="share" type="monotone" dataKey="Share Value" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12">
              <BarChart3 className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400 font-medium">No NAV data recorded yet</p>
              <p className="text-xs text-gray-300 mt-1">Click "Add Record" to start tracking Net Asset Value and Share Price</p>
            </div>
          )}
        </div>
        {/* History Table */}
        {history.length > 0 && (
          <div className="border-t border-gray-100 px-4 pb-4">
            <table className="w-full text-sm mt-3">
              <thead>
                <tr className="text-xs text-gray-400 uppercase tracking-wider">
                  <th className="text-left py-2">Date</th>
                  <th className="text-right py-2">Assets</th>
                  <th className="text-right py-2">Liabilities</th>
                  <th className="text-right py-2">NAV</th>
                  <th className="text-right py-2">Shares</th>
                  <th className="text-right py-2">Share Value</th>
                  <th className="text-right py-2">Notes</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {[...history].reverse().map(r => (
                  <tr key={r.id} className="border-t border-gray-50 hover:bg-gray-50" data-testid={`nav-row-${r.id}`}>
                    <td className="py-2 text-gray-600">{r.date}</td>
                    <td className="py-2 text-right text-gray-600">{r.total_assets != null ? fmtCurrency(r.total_assets) : '-'}</td>
                    <td className="py-2 text-right text-gray-600">{r.total_liabilities != null ? fmtCurrency(r.total_liabilities) : '-'}</td>
                    <td className="py-2 text-right font-medium text-teal-700">{r.nav != null ? fmtCurrency(r.nav) : '-'}</td>
                    <td className="py-2 text-right text-gray-600">{r.shares_outstanding != null ? new Intl.NumberFormat('en-US').format(r.shares_outstanding) : '-'}</td>
                    <td className="py-2 text-right font-medium text-indigo-700">{r.share_value != null ? fmtCurrency(r.share_value) : '-'}</td>
                    <td className="py-2 text-right text-gray-400 text-xs truncate max-w-[100px]">{r.notes || ''}</td>
                    <td className="py-2">
                      <div className="flex gap-1">
                        <button onClick={() => setEditRecord(r)} className="p-1 hover:bg-gray-100 rounded"><Pencil className="w-3.5 h-3.5 text-gray-400" /></button>
                        <button onClick={() => setDeleteTarget(r)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NavDialog
        open={addOpen || !!editRecord}
        onOpenChange={(v) => { if (!v) { setAddOpen(false); setEditRecord(null); } }}
        record={editRecord}
        companyId={companyId}
        onSave={onRefresh}
      />
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete NAV Record</AlertDialogTitle>
            <AlertDialogDescription>Delete NAV record for {deleteTarget?.date}?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function DashboardPage() {
  const { currentCompany } = useCompany();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const { handleExportCSV, handleImportCSV, exporting, importing } = useExportImport({
    exportUrl: '/api/dashboard/export/csv',
    importUrl: '/api/expenses/import/csv',
    companyId: currentCompany?.id,
    onImportSuccess: () => fetchDashboardStats(),
    entityName: 'dashboard_summary'
  });

  const fetchDashboardStats = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/analytics/dashboard`, {
        params: { company_id: currentCompany.id },
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  const isVeriquant = stats?.company_name?.toLowerCase() === 'veriquant';
  const isSaddlebrook = stats?.company_name?.toLowerCase() === 'saddlebrook';

  if (!currentCompany) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Please select a company</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Overview of {currentCompany.name}</p>
        </div>
        <ExportImportButtons onExportCSV={handleExportCSV} onImportCSV={handleImportCSV} exporting={exporting} importing={importing} testIdPrefix="dashboard" />
      </div>

      {/* Veriquant NAV & Share Value Analytics */}
      {isVeriquant && (
        <NavAnalyticsSection navData={stats?.nav_data} companyId={currentCompany.id} onRefresh={fetchDashboardStats} />
      )}

      {/* Veriquant cap table preview */}
      {isVeriquant && (
        <CapTablePreviewCard companyId={currentCompany.id} />
      )}

      {/* Company Details — quick access to EIN, formation, tax docs */}
      <CompanyDetailsCard company={currentCompany} />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Saddlebrook: Total Owed card (replaces "Total Files" slot for prominence) */}
        {isSaddlebrook && (
          <div className="bg-gradient-to-br from-rose-50 to-white rounded-xl border border-rose-200 p-5 shadow-sm" data-testid="stat-total-owed">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rose-700 font-medium">Total Owed to Store Owners</p>
                <p className="text-2xl font-semibold mt-1 text-rose-900">{formatCurrency(stats?.total_balance_due || 0)}</p>
                <p className="text-xs text-rose-600 mt-1">Outstanding balances across {stats?.total_clients || 0} owners</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
                <Coins className="w-5 h-5 text-rose-700" />
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-clients">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Clients</p>
              <p className="text-2xl font-semibold mt-1">{stats?.total_clients || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-expenses">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Monthly Expenses</p>
              <p className="text-2xl font-semibold mt-1">{formatCurrency(stats?.monthly_expenses || 0)}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-files">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Files</p>
              <p className="text-2xl font-semibold mt-1">{stats?.total_files || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stat-categories">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Expense Categories</p>
              <p className="text-2xl font-semibold mt-1">{stats?.expense_by_category?.length || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-medium text-gray-900">Recent Expenses</h2>
          </div>
          <div className="p-4">
            {stats?.recent_expenses?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_expenses.map((expense) => (
                  <div key={expense.id} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{expense.description}</p>
                      <p className="text-xs text-gray-500">{expense.date} · {expense.category}</p>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(expense.amount)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Receipt className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No recent expenses</p>
              </div>
            )}
          </div>
        </div>

        {/* Expense by Category */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-medium text-gray-900">Expenses by Category</h2>
          </div>
          <div className="p-4">
            {stats?.expense_by_category?.length > 0 ? (
              <div className="space-y-3">
                {stats.expense_by_category.map((cat, i) => (
                  <div key={i} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-500" style={{ backgroundColor: `hsl(${i * 60}, 60%, 50%)` }} />
                      <span className="text-sm text-gray-700">{cat.category}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(cat.total)}</p>
                      <p className="text-xs text-gray-500">{cat.count} expense{cat.count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <TrendingUp className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No expense data</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Receipt(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 17.5v-11"/></svg>
  );
}
